import { Helmet } from "react-helmet";
import { SideBar } from "../side-bar/SideBar";
import HrManagementHeader from "./header/HRManagementHeader";
import { api, getToken } from "../../../services/apis/auth-api/apiUrl";
import { ChangeEvent, useEffect, useState } from "react";
import { DOWNLOAD_LEAVE_REPORT, GET_LEAVE_CREDITS_END_POINT, UPDATE_VL_CRED_BY_ID, UPDATE_SL_CRED_BY_ID } from "../../../config/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import Modal from "../modal/modal";
import EmpLeaveDetails from "../modal/hr-management/EmpLeaveDetails";

interface empleaveCreditsProps {
    emp_id : number;
    fullname : string;
    total_leave : number;
    earned_vl : number;
    total_vl : number;
    total_sl : number;
    used_vl : number;
    used_sl : number;
    remaining_vl : number;
    remaining_sl : number;
}

interface extendedEmployeeLeaveCreditsProps extends empleaveCreditsProps {
    editted_vl_credits : number;
    editted_sl_credits : number;
    vl_clicked : number;
    sl_clicked : number;
}

const HrManagementLeaveCredits = () => {
    const [leaveCredits, setLeaveCredits] = useState<extendedEmployeeLeaveCreditsProps[] | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalChildren, setModalChildren] = useState<React.JSX.Element | null>(null);
    const [dateRange, setDateRange] = useState({start : '', end : ''});
    const [dlBtnLoading, setDlBtnLoading] = useState<boolean>(false);

    useEffect(()=>{
        const getLeaveCredits = async() => {
            try{
                const leaveCredits = await api.get(GET_LEAVE_CREDITS_END_POINT, {
                    headers: { 'Authorization': 'Bearer ' + getToken() }
                });
                const credits = leaveCredits.data as empleaveCreditsProps[];

                const extendedCredits = credits.map(cred => (
                    {
                        ...cred, 
                        editted_vl_credits : cred.total_vl,
                        editted_sl_credits : cred.remaining_sl,
                        vl_clicked : 0,
                        sl_clicked : 0
                    })) as extendedEmployeeLeaveCreditsProps[];
                setLeaveCredits(extendedCredits);
            }catch(e){}
        }
        getLeaveCredits();
    },[]);

    const createModalContent = (content : React.JSX.Element) =>{
        setModalChildren(content);
        setShowModal(true);
    }

    const handleDoubleClick = (emp_id : number, type : string) => {
        if(leaveCredits){
            const currentClick = leaveCredits.map(lc => (
                lc.emp_id === emp_id && type === 'vl'
                ? {...lc, vl_clicked : lc.vl_clicked + 1}
                 : lc.emp_id === emp_id && type === 'sl' ?
                 {...lc, sl_clicked : lc.sl_clicked + 1} : lc
            ));
            setLeaveCredits(currentClick);
        }
    }

    const handleCloseEditting = (emp_id : number, type : string) => {
        if(leaveCredits){
            const currentClick = leaveCredits.map(lc => (
                lc.emp_id === emp_id && type === 'vl'
                ? {...lc, vl_clicked : 0, editted_vl_credits : lc.total_vl} : 
                lc.emp_id === emp_id && type === 'sl' ? {...lc, sl_clicked : 0, editted_sl_credits : lc.total_vl}
                : lc
            ));
            setLeaveCredits(currentClick);
        }
    }

    const handleEditLeaveCredits = (e : ChangeEvent<HTMLInputElement>, emp_id : number, l_type : string) => {
        let updated_lc = parseInt(e.target.value);
        if(leaveCredits){
            const currentClick = leaveCredits.map(lc => (
                lc.emp_id === emp_id && l_type === 'vl'
                ? {...lc, 
                    editted_vl_credits : updated_lc
                } : lc.emp_id === emp_id && l_type === 'sl' ? {
                    ...lc, 
                    editted_sl_credits : updated_lc
                } : lc
            ));
            setLeaveCredits(currentClick);
        }
    }

    const handleDownloadLeaveReport = async() => {
        const filename = `${dateRange.start} - ${dateRange.end} Leave Report.xlsx`;
        if(dateRange.start === '' || dateRange.end === '') return alert('Please set start and end date');
        setDlBtnLoading(true);
        try{
            const dl_response = await api.post(
                DOWNLOAD_LEAVE_REPORT,
                dateRange,
                {
                    responseType : 'blob',
                    headers : {'Authorization' : 'Bearer '+ getToken()}
                }
            );

            const url = window.URL.createObjectURL(new Blob([dl_response.data], {type : 'text/csv'}));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setDlBtnLoading(false);
        }catch(e){
            alert('Error downloading csv file');
            setDlBtnLoading(false);
        }
    }

    const handleUpdateLeaveCred = async(emp_id : number, l_type : string) => {
        if(leaveCredits){
            const leave_cred = leaveCredits.filter(cred => cred.emp_id === emp_id)[0];
            const updated_cred = l_type === 'vl' ? leave_cred.editted_vl_credits : leave_cred.editted_sl_credits;
            const end_point = l_type === 'vl' ? UPDATE_VL_CRED_BY_ID : UPDATE_SL_CRED_BY_ID;
            try{
                await api.put(end_point, {id : emp_id , updated_cred : updated_cred}, {
                    headers: { 'Authorization': 'Bearer ' + getToken() }
                });
                alert('Leave credit updated')
            }catch(e){
                alert('Something went wrong!')
            }
        }
    }

    return (
        <div className='content flex'>
            <Helmet>
                <title>Leave Credits</title>
            </Helmet>
            <SideBar />
            <div className='main-content py-4'>
                <HrManagementHeader />
                <div className="my-2 bg-gray-100">
                    <div className="p-2">
                        <h1>Leave Credits tracker</h1>
                    </div>
                    <div className="flex w-full items-center">
                        <div className="flex m-2 max-w-72">
                            <input type="text" placeholder="Search name..." className='text-box' />
                        </div>
                        <div className="ms-auto flex justify-end items-center gap-4 my-2">
                            <div className="flex gap-2">
                                <label>From:</label>
                                <input type="date" onChange={(e)=>setDateRange({...dateRange, start : e.target.value})} className="text-box text-sm h-7 cursor-pointer" />
                            </div>
                            <div className="flex gap-2">
                                <label>To:</label>
                                <input type="date" onChange={(e)=>setDateRange({...dateRange, end : e.target.value})} className="text-box text-sm h-7 cursor-pointer" />
                            </div>
                            <div>
                                {dlBtnLoading ? (
                                    <button type="button" className="button button-vcc-green text-sm bg-green-500 hover:bg-green-500 cursor-not-allowed">
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />&nbsp; Downloading....
                                    </button>
                                ) : (
                                    <button type="button" onClick={handleDownloadLeaveReport} className="button button-vcc-green text-sm">
                                        Download leave report
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-container h-full">
                        <table className="w-full h-full border">
                            <thead className="table-fixed-header border">
                                <tr className="bg-orange-200">
                                    <th className='table-headers font-medium'>Fullname</th>
                                    <th className='table-headers font-medium'>Annual Leave Credits</th>
                                    <th className='table-headers font-medium'>Earned Leave Credits</th>
                                    <th className='table-headers font-medium'>Total VL</th>
                                    <th className='table-headers font-medium'>Total SL</th>
                                    <th className='table-headers font-medium'>Used VL</th>
                                    <th className='table-headers font-medium'>Used SL</th>
                                    <th className='table-headers font-medium'>Remaining VL</th>
                                    <th className='table-headers font-medium'>Remaining SL</th>
                                    <th className='table-headers font-medium'>Action</th>
                                </tr>
                            </thead>
                            <tbody className="text-center">
                                {leaveCredits ? leaveCredits.map(cred=>(
                                    <tr className="hover:bg-orange-100">
                                        <td className='table-body px-2'>{cred.fullname}</td>
                                        <td className='table-body px-0 font-bold'>
                                           {cred.total_leave}
                                        </td>
                                        <td className='table-body px-2 text-green-600 font-bold'>+{cred.earned_vl} VL</td>
                                        <td className='table-body px-2 font-bold'>
                                            {cred.vl_clicked >= 2 ? (
                                                <div className="flex justify-center items-center gap-3">
                                                    <div>
                                                        <input type="number" onChange={(e) => handleEditLeaveCredits(e, cred.emp_id, 'vl')} className="w-16 text-center outline-none" value={cred.editted_vl_credits} />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button type="button" className="text-red-500" onClick={()=>handleCloseEditting(cred.emp_id, 'vl')}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                        <button type="button" onClick={()=>handleUpdateLeaveCred(cred.emp_id, 'vl')} className="text-green-500">
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </button>
                                                    </div>
                                                </div>
                                           ) : (
                                                <div onClick={()=>handleDoubleClick(cred.emp_id, 'vl')}>
                                                    {cred.total_vl}
                                                </div>
                                           )}
                                        </td>
                                        <td className='table-body px-2 font-bold'>
                                            {cred.sl_clicked >= 2 ? (
                                                <div className="flex justify-center items-center gap-3">
                                                    <div>
                                                        <input type="number" onChange={(e) => handleEditLeaveCredits(e, cred.emp_id, 'sl')} className="w-16 text-center outline-none" value={cred.editted_sl_credits} />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button type="button" className="text-red-500" onClick={()=>handleCloseEditting(cred.emp_id, 'sl')}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                        <button type="button" onClick={()=>handleUpdateLeaveCred(cred.emp_id, 'sl')} className="text-green-500">
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </button>
                                                    </div>
                                                </div>
                                           ) : (
                                                <div onClick={()=>handleDoubleClick(cred.emp_id, 'sl')}>
                                                    {cred.total_sl}
                                                </div>
                                           )}
                                        </td>
                                        <td className='table-body px-2'>{cred.used_vl}</td>
                                        <td className='table-body px-2'>{cred.used_sl}</td>
                                        <td className='table-body px-2 font-bold text-green-600'>{cred.remaining_vl + cred.earned_vl}</td>
                                        <td className='table-body px-2 font-bold text-green-600'>{cred.remaining_sl}</td>
                                        <td className='table-body px-2'>
                                            <button type="button" onClick={()=>createModalContent(<EmpLeaveDetails account_id={cred.emp_id} full_name={cred.fullname} />)} className="text-blue-500 hover:underline">
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                )) : Array.from({length : 4}).map((_, index) => (
                                    <tr key={index}>
                                        {[...Array(10)].map((_, colIndex) => (
                                            <td key={colIndex} className='table-body'>
                                                <div className="table-loading"></div>
                                            </td>
                                        ))}
                                    </tr>
                                ))
                                }
                            </tbody>
                        </table>    
                    </div>
                </div>
            </div>
            {showModal &&(
                <Modal onClose={()=>setShowModal(false)}> 
                    {modalChildren}
                </Modal>
            )}
        </div>
    );
}

export default HrManagementLeaveCredits;